/**
 * A MATRIZ DE PORTAIS (15/10/2026, aprovada pelo Heitor: aba "Portais", a primeira de Licitações → Automação).
 *
 * A verdade de "o que cada portal faz" mora no CÓDIGO do backend: um registro por integração, servido por
 * `GET /procurement/portais/capacidades` (Bastião). A tela só LÊ. Antes eram três cópias escritas à mão que envelheceram
 * (Fluxos do ComprasNet, Fluxos da aba ComprasNet, a grade de Acessos editável no formulário do portal).
 *
 * Ao lado do estado DECLARADO vem a EVIDÊNCIA: a última vez que a etapa rodou de verdade, lida do banco. "Operante" sem
 * nada em 30 dias vira "parado" aqui, derivado; o backend também pode mandar "parado" direto.
 */
export type EstadoDaEtapa = 'operante' | 'pronto_nao_exercido' | 'parado' | 'em_desenvolvimento' | 'nao_implementado';
export type FonteDoEncontrar = 'alerta_licitacao' | 'pncp' | 'api_do_portal';
export type ChaveDaEtapa = 'login' | 'encontrar' | 'cadastrar' | 'monitorar' | 'disputar';

export interface PassoDoFluxo {
  titulo: string;
  ator?: 'robo' | 'operador' | 'portal';
  detalhes?: string[];
  avisos?: string[];
  /**
   * Peso do passo (15/10/2026, Bastião): 'destrutivo' é o que não se desfaz ("Desistir compra remove TODAS as propostas"),
   * 'atencao' é o que pede cuidado. Ausente = normal. A tela NÃO pode deixar o destrutivo com a cara dos outros.
   */
  tom?: TomDoPasso;
}

export type TomDoPasso = 'normal' | 'atencao' | 'destrutivo';

export interface EvidenciaDaEtapa {
  /** ISO da última vez que a etapa rodou de verdade (null = nunca, ou não medido). */
  ultimaVezEm?: string | null;
  /** O que foi contado: "lances aceitos", "propostas enviadas", "mensagens", "itens". */
  oQue?: string;
  /** Quantas vezes nos últimos 30 dias. */
  n30d?: number | null;
  /** Falhas nos últimos 30 dias (propostas recusadas pelo portal, por exemplo). */
  falhas30d?: number | null;
  /** Aviso de evidência fraca, que vira o rótulo da célula ("quase sem efeito"). Curto: mora numa pílula. */
  aviso?: string | null;
  /** A explicação longa do aviso, que vai no `title` da célula (16/10/2026, combinado com o Bastião). */
  avisoDetalhe?: string | null;
  /**
   * O CÓDIGO do aviso (19/10/2026, backend #486): é por ele que a tela decide, nunca pelo texto, que é livre e tem
   * emoji no meio. Ausente em aviso escrito à mão ou ainda sem código: a tela trata como aviso comum.
   */
  codigo?: CodigoDeAviso | string | null;
}

/**
 * A lista FECHADA do backend (#486, #489). Código fora dela é tratado como aviso comum. `PARADO_30D` = "nada em 30 dias":
 * MEDIDO e zero (não é "não medido").
 */
export type CodigoDeAviso = 'DECLARACAO_DESMENTIDA' | 'NAO_MEDIDO_AGORA' | 'NAO_MEDIVEL' | 'SEM_LINHA_NO_REGISTRO' | 'PARADO_30D';

/**
 * Variante de uma etapa (15/10/2026): o fluxo muda por modalidade. No ComprasNet, o Cadastro tem 4 (Dispensa itens/grupos,
 * Pregão itens/grupos) e o Robô de lances outros 4. Os `passos` da etapa são os COMUNS; os da variante vêm depois.
 */
export interface VarianteDaEtapa {
  chave: string;
  titulo: string;
  subtitulo?: string;
  /**
   * A etiqueta curta da variante (16/10/2026, Bastião): `modalidadeId=8` identifica a modalidade no portal, e numa variante
   * de disputa ela diz "Fluxo 4 — testado 07/04", a única das oito que declara ter sido exercitada. É evidência: aparece.
   */
  meta?: string;
  passos: PassoDoFluxo[];
}

/** A tabela que compara as variantes, aspecto por aspecto (a "comparação" dos Fluxos de hoje). */
export interface ComparativoDaEtapa {
  colunas: string[];
  linhas: { aspecto: string; valores: string[] }[];
}

export interface EtapaDoPortal {
  estado: EstadoDaEtapa;
  /**
   * Só em `cadastrar`: COMO o HUB envia. `'grava_itens'` = grava os itens no portal e PARA; a confirmação final é
   * humana, na tela do portal (Licitanet, 18/10/2026). `'grava_e_envia'` = a mesma tela (prévia, placar), e o HUB também
   * CADASTRA a proposta nova, com confirmação (Licitanet, 19/10/2026; quem decide é `previa.exigeConfirmacao`).
   * Ausente ou `'completo'` = o envio robô de sempre.
   */
  modo?: string;
  /** Só em Encontrar: de onde vêm os itens. */
  fonte?: FonteDoEncontrar;
  servico?: string;
  limitacoes?: string[];
  passos?: PassoDoFluxo[];
  variantes?: VarianteDaEtapa[];
  comparativo?: ComparativoDaEtapa | null;
  evidencia?: EvidenciaDaEtapa | null;
}

export interface PortalDoRegistro {
  portal: string;
  nome: string;
  portalOrigem?: string[];
  portalIds?: number[];
  etapas: Partial<Record<ChaveDaEtapa, EtapaDoPortal>>;
  armadilhas?: { titulo: string; errado: string; certo: string }[];
  /**
   * Linha sem integração (15/10/2026, medição do Bastião): portal que RECEBE itens mas não tem integração (Fiorilli,
   * Megasoft…), ou a linha "(sem portal identificado)" dos itens sem `portalOrigem`. Aparecem em vez de serem omitidas:
   * omitir escondia 59 itens e os portais onde a IPCOM concorre sem robô. Não têm página.
   */
  semIntegracao?: boolean;
  /**
   * De onde vêm os itens do portal (16/10/2026, o registro real): no PORTAL, não na etapa Encontrar. A célula Encontrar lê
   * a da etapa (`fonte`) e, sem ela, esta.
   */
  fonteDoEncontrar?: FonteDoEncontrar[];
}

export const ETAPAS: { chave: ChaveDaEtapa; rotulo: string }[] = [
  { chave: 'login', rotulo: 'Login' },
  { chave: 'encontrar', rotulo: 'Encontrar' },
  { chave: 'cadastrar', rotulo: 'Proposta' },
  { chave: 'monitorar', rotulo: 'Monitorar' },
  { chave: 'disputar', rotulo: 'Disputar' },
];

const DIA_MS = 86_400_000;
export const DIAS_SEM_USO_PARA_PARADO = 30;

/**
 * A data da evidência, só quando é AAAA-MM-DD (com ou sem hora). DADO RUIM NÃO VIRA ESTADO (16/10/2026, produção): o
 * registro chegou com "Thu Oct 15" (o toString de um Date cortado em 10 letras), o browser leu como 2001, e TODA célula
 * com uso apareceu "parado há 304 meses · última undefined/undefined". Data que não se lê não deriva "parado"; a linha
 * diz "data ilegível". `null` = sem data; 'ilegivel' = veio algo que não é data.
 */
export function diaDaEvidencia(v: string | null | undefined): { iso: string; ms: number } | 'ilegivel' | null {
  if (v == null || v === '') return null;
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(v)) return 'ilegivel';
  const ms = Date.parse(v.length === 10 ? `${v}T12:00:00Z` : v.replace(' ', 'T'));
  return Number.isFinite(ms) ? { iso: v, ms } : 'ilegivel';
}

/** O estado que a célula mostra: o declarado, ou "parado" quando o operante não rodou em 30 dias. */
export function estadoEfetivo(e: EtapaDoPortal | undefined, agoraMs: number): EstadoDaEtapa {
  if (!e) return 'nao_implementado';
  if (e.estado !== 'operante') return e.estado;
  const dia = diaDaEvidencia(e.evidencia?.ultimaVezEm);
  if (dia && dia !== 'ilegivel' && agoraMs - dia.ms > DIAS_SEM_USO_PARA_PARADO * DIA_MS) return 'parado';
  return 'operante';
}

/** "há 12 dias", "há 1 mês", "há 3 meses". */
export function haQuanto(iso: string, agoraMs: number): string {
  const dias = Math.max(0, Math.floor((agoraMs - Date.parse(iso)) / DIA_MS));
  if (dias < 30) return dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
  const meses = Math.floor(dias / 30);
  return meses === 1 ? 'há 1 mês' : `há ${meses} meses`;
}

/**
 * Mais falhas que acertos em 30 dias (15/10/2026, medição do Bastião: BLL Disputar, 106 aceitos × 169 falhas). Pelo n30d
 * sozinho a célula parecia saudável.
 */
export function falhaMaisDoQueFunciona(e: EtapaDoPortal | undefined): boolean {
  const ev = e?.evidencia;
  return !!ev && typeof ev.falhas30d === 'number' && ev.falhas30d > (ev.n30d ?? 0);
}

/** O rótulo da célula, nas palavras da tabela que o Heitor aprovou. */
export function rotuloDaCelula(e: EtapaDoPortal | undefined, agoraMs: number): string {
  const estado = estadoEfetivo(e, agoraMs);
  if (estado === 'operante' && e?.evidencia?.aviso) return e.evidencia.aviso;
  if (estado === 'operante' && falhaMaisDoQueFunciona(e)) return 'mais falhas que acertos';
  switch (estado) {
    case 'operante': return 'funciona';
    case 'pronto_nao_exercido': return 'pronto, nunca usado';
    case 'parado': {
      const dia = diaDaEvidencia(e?.evidencia?.ultimaVezEm);
      return dia && dia !== 'ilegivel' ? `parado ${haQuanto(dia.iso, agoraMs)}` : 'parado';
    }
    case 'em_desenvolvimento': return 'em construção';
    default: return 'não tem';
  }
}

/**
 * O aviso do backend AO LADO do estado, quando o estado não é "operante" (16/10/2026, conferência pós-deploy). No operante o
 * aviso já é o rótulo; nos outros ele sumia no hover, e o Licitanet Encontrar dizia "pronto, nunca usado" com 11 itens
 * importados na mesma célula, enquanto o backend avisava "⛔ declaração desmentida". O estado declarado fica (é o que o
 * registro diz) e o aviso vai numa pílula do lado, com o porquê no title.
 */
export function avisoAoLado(e: EtapaDoPortal | undefined, agoraMs: number): string | null {
  const estado = estadoEfetivo(e, agoraMs);
  if (!e?.evidencia?.aviso || estado === 'operante') return null;
  // "⚠️ nada em 30 dias" ao lado de "parado há 1 mês" diz a mesma coisa duas vezes (BNC Proposta, PCP; conferência de
  // 16/10). Some no parado, PELO CÓDIGO (#489), nunca pela frase; num estado que não diz "parado" (pronto, nunca usado)
  // ele acrescenta e fica.
  if (e.evidencia.codigo === 'PARADO_30D' && estado === 'parado') return null;
  return e.evidencia.aviso;
}

export type TomDaCelula = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

/**
 * O tom do aviso, PELO CÓDIGO: "declaração desmentida" é o registro dizendo uma coisa e o banco outra, e vai em vermelho;
 * os demais (não medido, não medível, sem código) seguem em âmbar.
 */
export function tomDoAviso(ev: EvidenciaDaEtapa | null | undefined): 'danger' | 'warning' {
  return ev?.codigo === 'DECLARACAO_DESMENTIDA' ? 'danger' : 'warning';
}

export function tomDaCelula(e: EtapaDoPortal | undefined, agoraMs: number): TomDaCelula {
  const estado = estadoEfetivo(e, agoraMs);
  // L6 (24/10/2026, padrão da Central): "funciona" é o normal, neutro (como o "Operante" da L1); em desenvolvimento também.
  // A cor fica no que pede olho: parado, nunca usado, mais falhas que acertos, aviso e declaração desmentida.
  if (estado === 'operante') return e?.evidencia?.aviso ? tomDoAviso(e.evidencia) : falhaMaisDoQueFunciona(e) ? 'warning' : 'neutral';
  if (estado === 'pronto_nao_exercido' || estado === 'parado') return 'warning';
  if (estado === 'em_desenvolvimento') return 'neutral';
  return 'neutral';
}

const fmtDia = (iso: string) => {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}`;
};

/**
 * O que foi contado, no número certo (16/10/2026): o registro manda a CHAVE da métrica, no singular ("lance aceito"),
 * e a tela dizia "787 lance aceito". Plural quando o número não é 1 (zero também: "0 lances enviados"); o resto da
 * frase, se houver ("item importado pela API do portal"), fica como veio.
 */
const PLURAL_DA_METRICA: [string, string][] = [
  ['lance aceito', 'lances aceitos'],
  ['lance enviado', 'lances enviados'],
  ['proposta enviada', 'propostas enviadas'],
  ['item importado', 'itens importados'],
  ['item monitorado', 'itens monitorados'],
];
export function oQueNoNumero(oQue: string, n: number): string {
  if (n === 1) return oQue;
  const par = PLURAL_DA_METRICA.find(([um]) => oQue === um || oQue.startsWith(um + ' '));
  return par ? par[1] + oQue.slice(par[0].length) : oQue;
}

/**
 * O título do passo sem a numeração que veio escrita nele (16/10/2026): os Fluxos tinham "1. LOGIN", e a tela, que
 * numera os passos, mostrava "1. 1. LOGIN". Tira só um número no começo seguido de ponto ou parêntese.
 */
export function tituloDoPasso(titulo: string): string {
  return titulo.replace(/^\s*\d+\s*[.)]\s+/, '');
}

/**
 * A linha de evidência embaixo do rótulo: "20 enviadas · 6 falhas · última 14/10 · só 1 lote por sala".
 * `fontes`: as do portal, para a célula Encontrar quando a etapa não traz a dela. `todasAsLimitacoes`: a linha sem
 * integração não tem página, então a célula é o único lugar das limitações (a da linha sem portal são duas).
 */
export function linhaDaEvidencia(e: EtapaDoPortal | undefined, opcoes: { fontes?: FonteDoEncontrar[]; todasAsLimitacoes?: boolean } = {}): string {
  if (!e) return '';
  const partes: string[] = [];
  const FONTE: Record<string, string> = { alerta_licitacao: 'AlertaLicitação', pncp: 'PNCP', api_do_portal: 'API do portal' };
  const fontes = (e.fonte ? [e.fonte] : opcoes.fontes || []).filter((f) => FONTE[f]).map((f) => FONTE[f]);
  if (fontes.length) partes.push(`via ${fontes.length > 1 ? `${fontes.slice(0, -1).join(', ')} e ${fontes[fontes.length - 1]}` : fontes[0]}`);
  const ev = e.evidencia;
  if (ev?.n30d != null) partes.push(`${ev.n30d.toLocaleString('pt-BR')}${ev.oQue ? ' ' + oQueNoNumero(ev.oQue, ev.n30d) : ''}`);
  if (ev?.falhas30d) partes.push(`${ev.falhas30d} ${ev.falhas30d === 1 ? 'falha' : 'falhas'}`);
  const dia = diaDaEvidencia(ev?.ultimaVezEm);
  if (dia === 'ilegivel') partes.push('data ilegível');
  else if (dia) partes.push(`última ${fmtDia(dia.iso)}`);
  if (e.limitacoes?.length) partes.push(...(opcoes.todasAsLimitacoes ? e.limitacoes : e.limitacoes.slice(0, 1)));
  return partes.join(' · ');
}

/**
 * Um `portalOrigem` que existe no banco e NÃO tem linha na matriz (19/10/2026, backend #486, `GET portais/sem-linha`):
 * medido, 18 rótulos com 119 itens (o SEAP-PR, `compras_pr`, é um deles). Vem ordenado por itens.
 */
export interface RotuloSemLinha {
  portalOrigem: string;
  itens: number;
  codigo: 'SEM_LINHA_NO_REGISTRO' | string;
  aviso?: string;
}

/**
 * A resposta da `portais/sem-linha`. ⛔ Só um ARRAY é medição: qualquer outra coisa LANÇA, para virar "não consegui
 * medir". Lida como lista vazia, a tela diria "todo item tem linha na matriz", o oposto do que se sabe. (A falha do
 * backend já chega como 5xx com `MEDICAO_INDISPONIVEL`; isto cobre o 200 com corpo estranho.)
 */
export function lerSemLinha(resposta: any): RotuloSemLinha[] {
  if (!Array.isArray(resposta)) throw new Error('a resposta não é a lista de rótulos');
  return resposta.filter((r: any) => r && typeof r.portalOrigem === 'string' && Number(r.itens) > 0)
    .map((r: any) => ({ ...r, itens: Number(r.itens) }));
}

/** A carga da rota pode vir como array ou `{ data }`; o que não for portal com etapas é descartado. */
export function lerRegistro(resposta: any): PortalDoRegistro[] {
  const lista = Array.isArray(resposta) ? resposta : Array.isArray(resposta?.data) ? resposta.data : [];
  return lista.filter((p: any) => p && typeof p.portal === 'string' && p.etapas && typeof p.etapas === 'object');
}

/**
 * O portal do item tem ENVIO pelo HUB? (18/10/2026, SAAE Ituiutaba, item #15036: o "Enviar proposta" aparecia para um
 * item Licitanet, e o backend recusava "submit-proposal do ComprasNet não se aplica".) A resposta sai da MESMA fonte da
 * matriz: a etapa `cadastrar` DECLARADA do portal cujo `portalOrigem` contém o do item. Nunca de uma lista na tela.
 *
 * - 'sim': `operante` ou `pronto_nao_exercido`. ⚠️ O "parado" (30 dias sem uso) é aviso da matriz, não falta de envio:
 *   por isso o estado DECLARADO, não o efetivo.
 * - 'nao': `nao_implementado` / `em_desenvolvimento`, ou portal fora do registro (altq, bbmnet, email…: sem integração).
 * - 'desconhecido': o registro não carregou, ou veio VAZIO (registro sem portal nenhum não é registro: lê-lo como
 *   "ninguém envia" esconderia o botão de todos). A tela fica como era (com o botão); o backend recusa o que não é dele.
 * - 'grava_itens': há envio, no modo `cadastrar.modo === 'grava_itens'` — o HUB grava os itens e para; a confirmação
 *   final é do Heitor, no portal. A tela troca o "Enviar proposta" pelo "Gravar itens no portal". Decidido pelo MODO,
 *   nunca pelo nome do portal.
 */
export type EnvioPeloHub = 'sim' | 'nao' | 'desconhecido' | 'grava_itens';

export function envioPeloHub(registro: PortalDoRegistro[] | null | undefined, portalOrigem: string | null | undefined): EnvioPeloHub {
  if (!registro || registro.length === 0) return 'desconhecido';
  const chave = String(portalOrigem ?? '').trim().toLowerCase();
  const portal = registro.find((p) => (p.portalOrigem || []).some((o) => String(o ?? '').trim().toLowerCase() === chave));
  if (!portal) return 'nao';
  const cadastrar = portal.etapas?.cadastrar;
  const estado = cadastrar?.estado;
  if (estado !== 'operante' && estado !== 'pronto_nao_exercido') return 'nao';
  // Os dois modos da prévia do gravar-itens usam a mesma tela; gravar ou cadastrar quem diz é a prévia.
  return cadastrar?.modo === 'grava_itens' || cadastrar?.modo === 'grava_e_envia' ? 'grava_itens' : 'sim';
}

/** A frase de quem não tem envio pelo HUB: o caminho é o portal, e depois o "Já enviei manualmente". */
export const FRASE_SEM_ENVIO_PELO_HUB = 'Este portal não tem envio pelo HUB: envie no portal e clique em Já enviei manualmente.';
